import { Component, ReactNode } from "react"

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

/**
 * Error Boundary para manejar errores de chunk loading
 * Basado en: https://stackoverflow.com/questions/69300341
 *
 * Detecta errores como:
 * - "Failed to fetch dynamically imported module"
 * - "ERR_CONNECTION_CLOSED"
 * - "ERR_CONNECTION_RESET"
 *
 * Y recarga automáticamente la página para obtener los chunks correctos
 */
export class ChunkErrorHandler extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
    }
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    }
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // Detectar errores de chunk loading
    const isChunkLoadError =
      error.message?.includes("Failed to fetch dynamically imported module") ||
      error.message?.includes("ERR_CONNECTION_CLOSED") ||
      error.message?.includes("ERR_CONNECTION_RESET") ||
      error.message?.includes("Loading chunk") ||
      error.message?.includes("ChunkLoadError")

    if (isChunkLoadError) {
      console.warn(
        "🔄 Chunk loading error detectado, recargando página...",
        error.message
      )

      // Prevenir loop infinito de recargas
      const lastReload = sessionStorage.getItem("last-chunk-reload")
      const now = Date.now()

      if (!lastReload || now - parseInt(lastReload) > 10000) {
        // Solo recargar si han pasado más de 10 segundos desde la última recarga
        sessionStorage.setItem("last-chunk-reload", now.toString())

        // Limpiar cache y recargar
        if ("caches" in window) {
          caches.keys().then((names) => {
            names.forEach((name) => caches.delete(name))
          })
        }

        // Recargar sin cache
        window.location.reload()
      } else {
        console.error(
          "⚠️ Demasiadas recargas recientes, no se recargará automáticamente"
        )
        console.error(
          "💡 Por favor, recarga la página manualmente (Ctrl+Shift+R)"
        )
      }
    } else {
      // Para otros errores, solo loggear
      console.error("Error boundary caught:", error, errorInfo)
    }
  }

  render() {
    if (this.state.hasError) {
      const isChunkLoadError =
        this.state.error?.message?.includes(
          "Failed to fetch dynamically imported module"
        ) ||
        this.state.error?.message?.includes("ERR_CONNECTION_CLOSED") ||
        this.state.error?.message?.includes("ERR_CONNECTION_RESET") ||
        this.state.error?.message?.includes("Loading chunk") ||
        this.state.error?.message?.includes("ChunkLoadError")

      if (isChunkLoadError) {
        // Mostrar mensaje de recarga mientras se recarga la página
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100vh",
              fontFamily: "system-ui, -apple-system, sans-serif",
              padding: "20px",
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: "48px", marginBottom: "20px" }}>🔄</div>
            <h2 style={{ marginBottom: "10px" }}>Actualizando aplicación...</h2>
            <p style={{ color: "#666", marginBottom: "20px" }}>
              Hay una nueva versión disponible. Recargando...
            </p>
            <div
              className="spinner"
              style={{
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #3498db",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                animation: "spin 1s linear infinite",
              }}
            />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )
      }

      // Para otros errores, mostrar mensaje genérico
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            fontFamily: "system-ui, -apple-system, sans-serif",
            padding: "20px",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "20px" }}>⚠️</div>
          <h2 style={{ marginBottom: "10px" }}>Algo salió mal</h2>
          <p style={{ color: "#666", marginBottom: "20px" }}>
            Por favor, recarga la página para continuar.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "10px 20px",
              fontSize: "16px",
              backgroundColor: "#3498db",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
            }}
          >
            Recargar página
          </button>
        </div>
      )
    }

    return this.props.children
  }
}

export default ChunkErrorHandler
